<template>
  <v-container fluid class="pa-6">
    <!-- Breadcrumbs -->
    <div class="tcv-breadcrumbs-container">
      <Breadcrumbs />
    </div>

    <!-- Hero Header Card -->
    <v-card class="tcv-hero-card mb-6" elevation="6">
      <div class="tcv-hero-header">
        <div class="d-flex align-center">
          <v-avatar size="48" class="tcv-hero-logo mr-4">
            <v-icon dark size="26">mdi-chip</v-icon>
          </v-avatar>
          <div>
            <h1 class="tcv-hero-title">Technical Components</h1>
            <p class="tcv-hero-subtitle mb-0">
              Define the technical IACS component details for your certification
            </p>
          </div>
        </div>
      </div>

      <!-- Step Progress -->
      <div class="tcv-steps-bar">
        <div class="tcv-step tcv-step-done">
          <div class="tcv-step-circle tcv-step-circle-done">
            <v-icon size="18" color="white">mdi-check</v-icon>
          </div>
          <span class="tcv-step-label">Define a Subject of Certification</span>
        </div>
        <div class="tcv-step-line tcv-step-line-done"></div>
        <div class="tcv-step tcv-step-active">
          <div class="tcv-step-circle tcv-step-circle-active">2</div>
          <span class="tcv-step-label">Configure</span>
        </div>
        <div class="tcv-step-line"></div>
        <div class="tcv-step">
          <div class="tcv-step-circle">3</div>
          <span class="tcv-step-label">Review</span>
        </div>
      </div>
    </v-card>

    <!-- Form Card -->
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <v-card class="tcv-form-card" elevation="4">
          <v-form ref="serverForm" v-model="valid" lazy-validation>
            <v-card-text class="pa-6">
              <!-- Component Type -->
              <div class="tcv-field-group mb-5">
                <div class="tcv-field-label">
                  <v-icon small color="#1976d2" class="mr-1">mdi-cog-outline</v-icon>
                  Component Type
                </div>
                <v-select
                  v-model="componentType"
                  :items="[
                    'Software Application (SAR)',
                    'Embedded Device (EDR)',
                    'Host Device (HDR)',
                    'Network Device (NDR)',
                  ]"
                  label="Component Type"
                  outlined
                  dense
                />
              </div>

              <!-- OS/Firmware Info -->
              <div class="tcv-field-group mb-5">
                <div class="tcv-field-label">
                  <v-icon small color="#0097a7" class="mr-1">mdi-memory</v-icon>
                  OS / Firmware Info
                </div>
                <v-text-field v-model="osFirmwareInfo" outlined dense label="OS/Firmware Info" />
              </div>

              <!-- Communication Interfaces -->
              <div class="tcv-field-group mb-5">
                <div class="tcv-field-label">
                  <v-icon small color="#388e3c" class="mr-1">mdi-lan</v-icon>
                  Communication Interfaces
                </div>
                <v-text-field
                  v-model="communicationInterfaces"
                  outlined
                  dense
                  label="Communication Interfaces"
                />
              </div>

              <!-- Protocols -->
              <div class="tcv-field-group mb-5">
                <div class="tcv-field-label">
                  <v-icon small color="#7b1fa2" class="mr-1">mdi-swap-horizontal</v-icon>
                  Protocols
                </div>
                <v-text-field v-model="protocols" outlined dense label="Protocols" />
              </div>

              <!-- Security Features -->
              <div class="tcv-field-group mb-5">
                <div class="tcv-field-label">
                  <v-icon small color="#c62828" class="mr-1">mdi-shield-lock-outline</v-icon>
                  Security Features
                </div>
                <v-text-field v-model="securityFeatures" outlined dense label="Security Features" />
              </div>
            </v-card-text>

            <!-- Action Buttons -->
            <v-divider></v-divider>
            <v-card-actions class="pa-5 d-flex justify-center">
              <v-btn class="tcv-btn-clear mr-3" @click="clearForm">
                <v-icon left small>mdi-eraser</v-icon>
                Clear
              </v-btn>
              <v-btn class="tcv-btn-back mr-3" @click="$router.go(-1)">
                <v-icon left small>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              <v-btn class="tcv-btn-next" :loading="loading" @click="handleNext">
                Next
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import util from '@/mixins/util';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'TechnicalComponentsView',
  components: {
    Breadcrumbs,
  },
  mixins: [util],
  data() {
    return {
      loading: false,
      valid: false,
      componentType: '',
      osFirmwareInfo: '',
      communicationInterfaces: '',
      protocols: '',
      securityFeatures: '',
      certificationName: this.$route.query.name || '',
      certificationDescription: this.$route.query.description || '',
      subjectOfCertification: this.$route.params.subject || '',
      family: this.$route.query.family || '',
      origin: this.$route.query.origin || 'manual',
      threatSpiderStandard: this.$route.query.threatSpiderStandard || '',
    };
  },
  created() {
    // Get component and standard from SubjectOfCertification route
    const threatSpiderComponent = this.$route.query.threatSpiderComponent;
    const threatSpiderStandard = this.$route.query.threatSpiderStandard;

    if (threatSpiderComponent && threatSpiderStandard) {
      const component = threatSpiderComponent;

      // Set component type based on device type
      if (component.Class === 'IT,OT' && component.ITPlatform === 'Network') {
        this.componentType = 'Network Device (NDR)';
      }

      // Set OS/Firmware info from Label
      this.osFirmwareInfo = component.Label || '';

      // Set communication interfaces
      this.communicationInterfaces = component.ITPlatform || '';

      // Set protocols
      this.protocols = component.ITProperties || '';

      // Set security features/context
      this.securityFeatures = component.OperationalContext || '';

      // Set origin to spider since data comes from ThreatSpider
      this.origin = 'spider';

      console.log(threatSpiderStandard);
    }
  },
  mounted() {
    console.log('Certification Name:', this.certificationName);
    console.log('Certification Description:', this.certificationDescription);
    console.log('Subject of Certification:', this.subjectOfCertification);
    console.log('Family:', this.$route.query.family);
  },
  methods: {
    handleNext() {
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
        this.$router.push({
          name: 'Requirements',
          params: {
            subject: this.subject || this.$route.params.subject,
          },
          query: {
            name: this.certificationName,
            description: this.certificationDescription,
            family: this.family,
            componentType: this.componentType,
            osFirmwareInfo: this.osFirmwareInfo,
            communicationInterfaces: this.communicationInterfaces,
            protocols: this.protocols,
            securityFeatures: this.securityFeatures,
            origin: this.origin,
            threatSpiderStandard: this.threatSpiderStandard,
          },
        });
      }, 1000);
    },
    clearForm() {
      this.componentType = '';
      this.osFirmwareInfo = '';
      this.communicationInterfaces = '';
      this.protocols = '';
      this.securityFeatures = '';
      this.valid = false;
    },
  },
};
</script>

<style scoped>
/* ============================================
   TECHNICAL COMPONENTS VIEW - Modern Design
   ============================================ */

.tcv-breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 28px;
}

.tcv-breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Hero Card */
.tcv-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.tcv-hero-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 24px 28px;
}

.tcv-hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.tcv-hero-title {
  color: white;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.tcv-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.92rem;
  font-weight: 400;
}

/* Step Progress Bar */
.tcv-steps-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 28px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-top: 1px solid #e8eef3;
}

.tcv-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.tcv-step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  background: #e0e0e0;
  color: #9e9e9e;
  transition: all 0.3s ease;
}

.tcv-step-circle-active {
  background: linear-gradient(135deg, #00405e, #0097a7);
  color: white;
  box-shadow: 0 4px 14px rgba(0, 100, 148, 0.4);
}

.tcv-step-circle-done {
  background: linear-gradient(135deg, #388e3c, #66bb6a);
  color: white;
  box-shadow: 0 4px 14px rgba(56, 142, 60, 0.35);
}

.tcv-step-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #78909c;
}

.tcv-step-active .tcv-step-label {
  color: #00405e;
}

.tcv-step-done .tcv-step-label {
  color: #388e3c;
}

.tcv-step-line {
  flex: 1;
  max-width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #e0e0e0, #bdbdbd);
  border-radius: 2px;
  margin: 0 12px;
  margin-bottom: 22px;
}

.tcv-step-line-done {
  background: linear-gradient(90deg, #66bb6a, #a5d6a7);
}

/* Form Card */
.tcv-form-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.tcv-field-group {
  position: relative;
}

.tcv-field-label {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: #455a64;
  margin-bottom: 6px;
  letter-spacing: 0.3px;
}

/* Action Buttons */
.tcv-btn-clear {
  background: linear-gradient(135deg, #f37d21 0%, #ff9800 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
  box-shadow: 0 4px 12px rgba(243, 125, 33, 0.3) !important;
  transition: all 0.3s ease !important;
}
.tcv-btn-clear:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(243, 125, 33, 0.5) !important;
}

.tcv-btn-back {
  background: linear-gradient(135deg, #78909c 0%, #90a4ae 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
  box-shadow: 0 4px 12px rgba(120, 144, 156, 0.3) !important;
  transition: all 0.3s ease !important;
}
.tcv-btn-back:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(120, 144, 156, 0.5) !important;
}

.tcv-btn-next {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 24px !important;
  box-shadow: 0 4px 14px rgba(0, 100, 148, 0.4) !important;
  transition: all 0.3s ease !important;
}
.tcv-btn-next:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 22px rgba(0, 100, 148, 0.6) !important;
}

/* Responsive */
@media (max-width: 960px) {
  .tcv-hero-header {
    padding: 20px;
  }
  .tcv-hero-title {
    font-size: 1.3rem;
  }
  .tcv-steps-bar {
    padding: 16px;
  }
}
</style>
